<script setup lang="ts">
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue'
import {
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  VisuallyHidden,
} from 'reka-ui'
import { cn } from '@ds/lib/utils'
import { DSKbd, DSKbdGroup } from '@ds'
import { CornerDownLeftIcon, SearchIcon, SearchXIcon, XIcon } from '@lucide/vue'
import { SearchDialogFilter, SearchDialogSection } from './types'

type Props = {
  open: boolean
  query: string
  filters: SearchDialogFilter[]
  activeFilterKey: string
  sections: SearchDialogSection[]
  /** What "start typing" is scoped to, e.g. "assets, tags and transactions" or one filter's label. */
  scopeLabel: string
  placeholder?: string
}

const { open, query, filters, activeFilterKey, sections, scopeLabel, placeholder = '' } = defineProps<Props>()

const emit = defineEmits<{
  'update:open': [value: boolean]
  'update:query': [value: string]
  'update:activeFilterKey': [value: string]
  select: [key: string]
}>()

const inputRef = useTemplateRef('inputRef')

const selectedIndex = ref(0)

const flatRows = computed(() => sections.flatMap(section => section.items))
const selectedKey = computed(() => flatRows.value[selectedIndex.value]?.key)
const hasQuery = computed(() => query.trim().length > 0)
const hasResults = computed(() => sections.some(section => section.items.length))

watch(() => [query, activeFilterKey], () => {
  selectedIndex.value = 0
})

const highlightParts = (text: string) => {
  const needle = query.trim()
  if (!needle) return [{ text, highlighted: false }]
  const haystack = text.toLowerCase()
  const lowerNeedle = needle.toLowerCase()
  const parts: { text: string; highlighted: boolean }[] = []
  let cursor = 0
  while (cursor < text.length) {
    const matchAt = haystack.indexOf(lowerNeedle, cursor)
    if (matchAt < 0) {
      parts.push({ text: text.slice(cursor), highlighted: false })
      break
    }
    if (matchAt > cursor) parts.push({ text: text.slice(cursor, matchAt), highlighted: false })
    parts.push({ text: text.slice(matchAt, matchAt + needle.length), highlighted: true })
    cursor = matchAt + needle.length
  }
  return parts
}

const handleOpenAutoFocus = (event: Event) => {
  event.preventDefault()
  nextTick(() => inputRef.value?.focus())
}

const handleClear = () => {
  emit('update:query', '')
}

const handleSelectRow = (key: string) => {
  emit('select', key)
}

const handleHoverRow = (index: number) => {
  selectedIndex.value = index
}

const cycleFilter = (reverse: boolean) => {
  if (!filters.length) return
  const currentIndex = filters.findIndex(filter => filter.key === activeFilterKey)
  const nextIndex = (currentIndex + (reverse ? -1 : 1) + filters.length) % filters.length
  emit('update:activeFilterKey', filters[nextIndex].key)
}

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'ArrowDown' && flatRows.value.length) {
    event.preventDefault()
    selectedIndex.value = (selectedIndex.value + 1) % flatRows.value.length
  } else if (event.key === 'ArrowUp' && flatRows.value.length) {
    event.preventDefault()
    selectedIndex.value = (selectedIndex.value - 1 + flatRows.value.length) % flatRows.value.length
  } else if (event.key === 'Tab') {
    event.preventDefault()
    cycleFilter(event.shiftKey)
  } else if (event.key === 'Enter') {
    if (selectedKey.value) handleSelectRow(selectedKey.value)
  }
}
</script>

<template>
  <DialogRoot
    :open
    @update:open="emit('update:open', $event)"
  >
    <DialogPortal>
      <DialogOverlay
        class="fixed inset-0 z-50 bg-scrim backdrop-blur-[2px] flex justify-center items-start pt-18 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
      />
      <DialogContent
        :class="cn(
          'fixed inset-0 sm:inset-auto sm:left-1/2 sm:top-18 z-50 w-full sm:w-170 h-full sm:h-auto max-h-full sm:max-h-180 sm:-translate-x-1/2',
          'font-ds flex flex-col overflow-hidden',
          'bg-card text-card-foreground rounded-none sm:rounded-ds-xl shadow-ds-xl',
          'duration-ds-normal data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        )"
        @open-auto-focus="handleOpenAutoFocus"
      >
        <VisuallyHidden as-child>
          <DialogTitle>Search</DialogTitle>
        </VisuallyHidden>
        <VisuallyHidden as-child>
          <DialogDescription>Search {{ scopeLabel }}</DialogDescription>
        </VisuallyHidden>

        <div class="flex items-center gap-3 min-h-15 pt-safe pr-4 pl-5 border-b border-border shrink-0">
          <SearchIcon
            class="size-5 text-brand shrink-0"
            aria-hidden="true"
          />
          <input
            ref="inputRef"
            :value="query"
            :placeholder
            data-refid="searchDialogInput"
            class="flex-1 min-w-0 border-0 outline-none bg-transparent font-ds text-[17px] font-medium text-foreground placeholder:text-foreground/35 ring-0"
            @input="emit('update:query', ($event.target as HTMLInputElement).value)"
            @keydown="handleKeydown"
          >
          <button
            v-if="hasQuery"
            type="button"
            class="flex items-center justify-center size-7 rounded-ds-sm text-muted-foreground hover:bg-background cursor-pointer shrink-0"
            @click="handleClear"
          >
            <XIcon
              class="size-4"
              aria-hidden="true"
            />
          </button>
          <DSKbd class="hidden sm:inline-flex">
            esc
          </DSKbd>
          <button
            type="button"
            class="sm:hidden text-[15px] font-bold text-brand shrink-0 cursor-pointer"
            @click="emit('update:open', false)"
          >
            Cancel
          </button>
        </div>

        <div class="flex gap-1.5 px-4 py-3 border-b border-border shrink-0 overflow-x-auto no-scrollbar">
          <button
            v-for="filter in filters"
            :key="filter.key"
            type="button"
            class="flex items-center gap-1.5 h-7.5 px-3 rounded-full text-[13px] font-bold whitespace-nowrap cursor-pointer transition-colors duration-ds-fast ease-ds-standard"
            :class="filter.key === activeFilterKey ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground hover:bg-muted'"
            :data-refid="`searchDialogFilter${filter.key.replace(' ', '')}`"
            :data-active="filter.key === activeFilterKey"
            @click="emit('update:activeFilterKey', filter.key)"
          >
            {{ filter.label }}
            <span
              v-if="filter.count !== undefined"
              class="text-[12px] font-bold"
              :class="filter.key === activeFilterKey ? 'text-primary-foreground/70' : 'text-muted-foreground'"
            >{{ filter.count }}</span>
          </button>
        </div>

        <div class="flex-1 min-h-0 overflow-y-auto p-1.5 pb-safe sm:pb-1.5">
          <template v-if="hasResults">
            <div
              v-for="(section, sectionIndex) in sections"
              :key="section.key"
            >
              <div
                v-if="section.items.length"
                class="px-3 pt-3 pb-1.5"
              >
                <span class="text-[11px] font-bold uppercase tracking-ds-wide text-muted-foreground">{{ section.label }}</span>
              </div>
              <button
                v-for="(row, rowIndex) in section.items"
                :key="row.key"
                type="button"
                class="flex items-center gap-3 w-full px-3 py-2.25 rounded-ds-md text-left cursor-pointer"
                :class="row.key === selectedKey ? 'bg-accent' : 'hover:bg-background'"
                :data-refid="`searchDialogSection${sectionIndex + 1}Item${rowIndex + 1}`"
                @click="handleSelectRow(row.key)"
                @mouseenter="handleHoverRow(flatRows.findIndex(flatRow => flatRow.key === row.key))"
              >
                <span
                  class="flex items-center justify-center size-8.5 rounded-ds-sm shrink-0"
                  :class="row.key === selectedKey ? 'bg-card text-brand' : 'bg-secondary text-muted-foreground'"
                >
                  <component
                    :is="row.icon"
                    class="size-4"
                    aria-hidden="true"
                  />
                </span>
                <span class="flex-1 min-w-0 flex flex-col gap-0.5">
                  <span class="text-[15px] font-medium text-foreground truncate">
                    <template
                      v-for="(part, index) in highlightParts(row.title)"
                      :key="index"
                    ><span :class="part.highlighted ? 'text-brand font-bold' : undefined">{{ part.text }}</span></template>
                  </span>
                  <span
                    v-if="row.subtitle"
                    class="text-[13px] font-medium text-muted-foreground truncate"
                  >
                    <template
                      v-for="(part, index) in highlightParts(row.subtitle)"
                      :key="index"
                    ><span :class="part.highlighted ? 'text-brand font-bold' : undefined">{{ part.text }}</span></template>
                  </span>
                </span>
                <span
                  v-if="row.key === selectedKey"
                  class="flex items-center gap-1.5 text-[12px] font-bold text-brand shrink-0"
                >
                  Open
                  <CornerDownLeftIcon
                    class="size-3.5"
                    aria-hidden="true"
                  />
                </span>
              </button>
            </div>
          </template>
          <div
            v-else-if="hasQuery"
            class="flex flex-col items-center gap-2 py-12 px-6 text-center"
          >
            <SearchXIcon
              class="size-6 text-muted-foreground/60"
              aria-hidden="true"
            />
            <span class="text-[15px] font-bold text-foreground">No results for &ldquo;{{ query }}&rdquo;</span>
            <span class="text-[13px] font-medium text-muted-foreground">Try a ticker, a name or a tag</span>
          </div>
          <div
            v-else
            class="flex flex-col items-center gap-2 py-12 px-6 text-center"
          >
            <SearchIcon
              class="size-6 text-muted-foreground/60"
              aria-hidden="true"
            />
            <span class="text-[15px] font-bold text-foreground">Search {{ scopeLabel }}</span>
            <span class="text-[13px] font-medium text-muted-foreground">Type an asset name, ticker or tag</span>
          </div>
        </div>

        <div class="hidden sm:flex items-center gap-4.5 h-11 px-5 border-t border-border bg-background text-[12px] font-medium text-muted-foreground shrink-0">
          <span class="flex items-center gap-1.5">
            <DSKbdGroup>
              <DSKbd class="bg-card">↑</DSKbd>
              <DSKbd class="bg-card">↓</DSKbd>
            </DSKbdGroup>
            Navigate
          </span>
          <span class="flex items-center gap-1.5">
            <DSKbd class="bg-card">↵</DSKbd>
            Open
          </span>
          <span class="flex items-center gap-1.5">
            <DSKbd class="bg-card">tab</DSKbd>
            Switch filter
          </span>
        </div>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>
